/**
 * The explored minimap (integration plan slice 4), lifted from the
 * dungeon-floors prototype's `dungeon-minimap.js`: a small 2D canvas (not
 * WebGL and not text: ADR 0005 keeps text out of canvases, and this draws
 * none) with the explored tiles of the current floor, north up, and the
 * leader as a dot. It draws only what the player has seen: the tiles the
 * renderer's fog of war has explored (`explored`, per tile index) and the
 * leader's tile; no other unit is ever drawn, so nothing the fog hides
 * leaks. Colours come from the theme through CSS variables the shell sets
 * (`--ab-map-wall`, `--ab-map-floor`, `--ab-map-exit`, `--ab-map-leader`,
 * `--ab-map-edge`); the "explored" label and the north mark from `ui.map.*`
 * keys, as DOM over the canvas. The tiles are drawn once per change into a
 * cached layer, then the dot over it (the prototype's background cache).
 */
import { useEffect, useRef } from 'react';
import type { Floor, Pos } from '@ab/game-core';
import type { Strings } from '@ab/session';
import { text } from './text.ts';
import { uiTiming } from './timing.ts';
import './minimap.css';

export type MinimapProps = {
  /** The floor the explored tiles belong to, with the leader's tile; null before a floor is shown. */
  readonly map: { readonly floor: Floor; readonly explored: readonly boolean[]; readonly leader: Pos | null } | null;
  readonly strings: Strings;
};

const cssColor = (el: Element, name: string): string => getComputedStyle(el).getPropertyValue(name).trim();

const draw = (canvas: HTMLCanvasElement, map: NonNullable<MinimapProps['map']>): void => {
  const m = uiTiming.minimap;
  const dpr = Math.min(window.devicePixelRatio || 1, m.maxPixelRatio);
  const w = Math.round(canvas.clientWidth * dpr);
  const h = Math.round(canvas.clientHeight * dpr);
  if (w === 0 || h === 0) return;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  if (ctx === null) return;
  const { floor, explored, leader } = map;
  ctx.clearRect(0, 0, w, h);
  if (explored.length !== floor.width * floor.height) return;
  const margin = m.margin * dpr;
  const scale = Math.max(0, Math.min((w - 2 * margin) / floor.width, (h - 2 * margin) / floor.height));
  const ox = (w - floor.width * scale) / 2;
  const oy = (h - floor.height * scale) / 2;
  const colours = { wall: cssColor(canvas, '--ab-map-wall'), floor: cssColor(canvas, '--ab-map-floor'), exit: cssColor(canvas, '--ab-map-exit') };
  // Whole device pixels per tile edge, so neighbouring tiles meet without seams.
  for (let i = 0; i < explored.length; i += 1) {
    if (explored[i] !== true) continue;
    const tile = floor.tiles[i];
    ctx.fillStyle = tile === 'wall' ? colours.wall : tile === 'exit' ? colours.exit : colours.floor;
    const x = i % floor.width;
    const y = Math.floor(i / floor.width);
    const x0 = Math.floor(ox + x * scale);
    const y0 = Math.floor(oy + (floor.height - 1 - y) * scale);
    ctx.fillRect(x0, y0, Math.floor(ox + (x + 1) * scale) - x0, Math.floor(oy + (floor.height - y) * scale) - y0);
  }
  if (leader === null) return;
  ctx.beginPath();
  ctx.arc(ox + (leader.x + 0.5) * scale, oy + (floor.height - 0.5 - leader.y) * scale, m.dotRadius * dpr, 0, Math.PI * 2);
  ctx.fillStyle = cssColor(canvas, '--ab-map-leader');
  ctx.fill();
  ctx.lineWidth = m.dotOutline * dpr;
  ctx.strokeStyle = cssColor(canvas, '--ab-map-edge');
  ctx.stroke();
};

export const Minimap = ({ map, strings }: MinimapProps) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = canvas.current;
    if (c === null || map === null) return;
    draw(c, map);
    // The canvas's CSS size changes with the layout (a rotation): draw again at the new size.
    const observer = new ResizeObserver(() => {
      draw(c, map);
    });
    observer.observe(c);
    return () => {
      observer.disconnect();
    };
  }, [map]);
  return (
    <section className="ab-minimap" data-testid="minimap" aria-label={text(strings, 'ui.map.title')}>
      <canvas ref={canvas} role="img" aria-label={text(strings, 'ui.map.canvas')} />
      <span className="ab-minimap-label">{text(strings, 'ui.map.explored')}</span>
      <span className="ab-minimap-north" aria-label={text(strings, 'ui.map.north_aria')}>
        {text(strings, 'ui.map.north')}
      </span>
    </section>
  );
};
